import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './Home.css';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { isPremiumUser, isQuestionLocked } from '../utils/subscriptionUtils';
import UpgradeModal from '../components/UpgradeModal';
import { logTestAccess, updateTestProgress, trackQuestionAttempts, fetchWeakQuestions } from '../utils/testTracker';
import { PRACTICE_QUESTION_TIPS } from '../data/practiceQuestionTips';

const baseSampleQuestions = [
  {
    id: 1,
    text: 'Unless otherwise posted, what is the basic speed limit outside a city, town or village on a primary highway?',
    options: ['100 km/h', '80 km/h', '60 km/h', '50 km/h'],
    img: null,
    answerIndex: 0,
  },
  {
    id: 2,
    text: 'When backing a passenger vehicle to the left, a driver should:',
    options: ['Look in the inside rear-view mirror and make steering adjustments as necessary', 'Look over the right shoulder with occasional glances to the front', 'Look over the left shoulder with occasional glances to the front', 'Without shoulder checking, use the inside and outside rear-view mirrors to ensure the best view to the rear and both sides of the vehicle'],
    img: null,  
    answerIndex: 2,
  },
  {
    id: 3,
    text: 'When approaching an intersection and facing a traffic control light that is showing a green right-turn arrow and a red light, a driver',
    options: ['Must stop and wait for a green light before turning in the direction of the arrow', 'May go straight through the intersection without stopping', 'May proceed when safe, without stopping, in the direction of the arrow', 'May turn left without stopping'],
    img: null,
    answerIndex: 2,
  },
  {
    id: 4,
    text: 'Alberta has a demerit point system where a fully licenced (non-learner, non-probationary) driver is suspended when they accumulate',
    options: ['15 points', '8 points', '5 points', '12 points'],
    img: null,
    answerIndex: 0,
  },
  {
    id: 5,
    text: 'When passing a large truck on a two-way highway, a driver should',
    options: ['Leave extra room before returning to their lane', 'Return to their lane when signalled by the truck’s headlights', 'Return to their lane when the truck is no longer visible in any of their rear-view mirrors', 'Return to their lane once they have cleared the truck’s front bumper'],
    img: null,
    answerIndex: 0,
  },
  {
    id: 6,
    text: 'Which statement best describes the steps for turning right at a red traffic light?',
    options: ['Right turns on a red traffic light are never permitted', 'Signal and make sure there is no sign prohibiting the turn, stop, look left and right, yield the right-of-way, then proceed', 'Reduce speed before the turn, look right for cyclists, signal and proceed', 'Signal and reduce speed before the turn, look left, yield to vehicles and proceed'],
    img: null,
    answerIndex: 1,
  },
  {
    id: 7,
    text: 'This sign indicates:',
    options: ['Route for vehicles over 4500 kg', 'Route for slow moving vehicles', 'Reserved lane for buses', 'Dangerous goods route'],
    img: 'assets/7.png',
    answerIndex: 3,
  },
  {
    id: 8,
    text: 'A single solid white line between driving lanes in an urban area means',
    options: ['It is a line separating traffic travelling in opposite directions', 'Traffic on the right is moving faster than traffic on the left', 'Lane changing is not permitted', 'Lane change is permitted'],
    img: null ,
    answerIndex: 2
  },
  {
    id: 9,
    text: 'A “parking lane” as defined in the Basic Licence Driver’s Handbook is:',
    options: ['A lane closest to the curb, where parking is permitted but there are no meters', 'All of these responses are correct', 'That portion of a primary highway between the edge of the roadway to the right of the direction of traffic and the nearest solid white line, not being the centre line, marked on the roadway', 'A lane immediately beside a row of parking meters in an urban area'],
    img: null ,
    answerIndex: 2
  },
  {
    id: 10,
    text: 'Shoulder checking means:',
    options: ['Glancing towards the blind spot in the direction the driver intends to move', 'Glancing into the left outside mirror', 'Glancing into both the inside and outside mirrors', 'Glancing into the right outside mirror'],
    img: null ,
    answerIndex: 0
  },
  {
    id: 11,
    text: 'This sign indicates:',
    options: ['Off-road facilities ahead', 'Slippery when wet', 'Uneven pavement', 'Detour ahead'],
    img: 'assets/11.png' ,
    answerIndex: 2
  },
  {
    id: 12,
    text: 'A traffic-lane control light with an illuminated red “X” means:',
    options: ['Prepare for an emergency vehicle travelling in that lane', 'Reduce speed to below 30 km/h', 'Use that lane only for the purpose of making left turns', 'Travel is not permitted in that lane'],
    img: 'assets/12.png' ,
    answerIndex: 3
  },
  {
    id: 13,
    text: 'Does a parent have the right to withdraw a previously given “parental consent” for someone under 18 years of age with respect to an operator’s licence?',
    options: ['Yes, provided the person is still under the age of 18 and is not self-supporting or married', 'Yes, provided that both parents verbally agree to withdraw the consent at a registry office', 'No, once it is given it remains in effect until the person reaches 18 years of age', 'No, but another consent form must be signed by both parents after a licence suspension'],
    img: null,
    answerIndex: 0
  },
  {
    id: 14,
    text: 'When approaching a Yield sign, you must:',
    options: ['Yield the right-of-way to traffic not facing the sign and all pedestrians in the intersection', 'Always sound the horn to warn other traffic within the intersection', 'Expect all other vehicles to yield the right-of-way to you', 'Come to a stop and proceed after looking to the right and left'],
    img: null,
    answerIndex: 0
  },
  {
    id: 15,
    text: 'The term “over-driving” your headlights at night means:',
    options: ['The driver cannot see clearly for 1000 metres', 'The low beam headlights are not set properly', 'The driver is travelling at a speed which would not permit them to see an object in time to stop', 'The driver has had the headlights on for too long a time'],
    img: null,
    answerIndex: 2
  },
  {
    id: 16,
    text: 'Under the Graduated Driver Licensing program, a driver will receive 2 demerits:',
    options: ['For having more passengers than there are seat belts', 'For not wearing a seat belt securely fastened', 'For not wearing a seat belt', 'For not wearing a seat belt properly adjusted'],
    img: null,
    answerIndex: 0
  },
  {
    id: 17,
    text: 'Using the 2-second rule to judge a safe following distance when driving:',
    options: ['Is more accurate on rural highways than in urban centres', 'Is not accurate at speeds greater than 100 km/h', 'Is accurate at any speed', 'Is not accurate at speeds lower than 30 km/h'],
    img: null,
    answerIndex: 2
  },
  {
    id: 18,
    text: 'This sign indicates:',
    options: ['Traffic entering from the right', 'Winding road ahead', 'Curve right', 'Concealed intersection on the right'],
    img: './assets/18.png',
    answerIndex: 2
  },
  {
    id: 19,
    text: 'A driver approaching a flashing red traffic light must:',
    options: ['Stop and wait for the flashing green light', 'Reduce speed and proceed with caution', 'Stop and proceed only when safe to do so', 'Stop until the signal light turns green'],
    img: null ,
    answerIndex: 2
  },
  {
    id: 20,
    text: 'This sign indicates:',
    options: ['Do not enter', 'Slow moving vehicles ahead', 'Warning, Yield sign ahead', 'Warning, traffic is approaching'],
    img: './assets/20.png',
    answerIndex: 2
  },
  {
    id: 21,
    text: 'According to the law in Alberta, it is the driver’s responsibility to ensure that all passengers under what age are properly restrained in the vehicle?:',
    options: ['18 years', '24 years', '21 years', '16 years'],
    img: null ,
    answerIndex: 3
  },
  {
    id: 22,
    text: 'When parallel parking, the vehicle’s wheels closest to the curb must be within what maximum distance?',
    options: ['80 centimetres from the curb', '30 centimetres from the curb', '10 centimetres from the curb', '50 centimetres from the curb'],
    img: null ,
    answerIndex: 3
  },
  {
    id: 23,
    text: 'Who is responsible and legally obligated to report any medical condition, change in health, or physical disability that may affect a driver’s ability to drive',
    options: ['The driver’s employer', 'The driver', 'The driver’s doctor', 'The driver’s insurance company'],
    img: null ,
    answerIndex: 1
  },
  {
    id: 24,
    text: 'This sign indicates:',
    options: ['Road narrows, left', 'Two-way traffic', 'Divided highway ends', 'Obstruction – keep left'],
    img: './assets/24.png', 
    answerIndex: 0
  },
   {
    id: 25,
    text: 'This sign indicates:',
    options: ['Rumble strips', 'Construction zone', 'Bump', 'Pavement ends'],
    img: './assets/25.png', 
    answerIndex: 2
  },
  {
    id: 26,
    text: 'Under the Graduated Driver Licensing program, what is the minimum amount of time that a person must serve as a probationary Class 5 driver?',
    options: ['6 months', '18 months', '2 years', '1 year'],
    img: null , 
    answerIndex: 2
  },
  {
    id: 27,
    text: 'A large “X” painted on the pavement indicates:',
    options: ['Construction ahead', 'The traffic lane is ending', 'Railway crossing ahead', 'Wild animal crossing ahead'],
    img: null , 
    answerIndex: 2
  },
  {
    id: 28,
    text: 'A driver is required to report all collisions to the police when persons have been injured or killed, or when combined damage to all vehicles or property is more than:',
    options: ['750', '1000', '250', '500'],
    img: null , 
    answerIndex: 1
  },
  {
    id: 29,
    text: 'When a driver is stopping behind another vehicle in traffic',
    options: ['Remain a minimum of 10 metres from the vehicle ahead', 'Allow sufficient space to move into another lane without reversing', 'Maintain sufficient space as determined by the 2-second rule', 'Keep back approximately 1 metre from the rear bumper of the vehicle ahead'],
    img: null , 
    answerIndex: 1
  },
  {
    id: 30,
    text: 'If another vehicle is following too close, what should a driver do?',
    options: ['Slow down gradually and encourage the other vehicle to pass', 'Press hard on the brake pedal', 'Increase speed to 10 km/h above the posted speed limit', 'Activate the four-way flashers'],
    img: null , 
    answerIndex: 0
  },
  {
    id: 31,
    text: 'When recovering from a skid, a driver should:',
    options: ['Accelerate and steer in the opposite direction of the skid', 'Brake and counter-steer', 'Steer in the direction the driver wants the front of the vehicle to go', 'Brake and hold firmly on the steering wheel'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 32,
    text: 'A “slow-moving vehicle” sign must be displayed on the rear of any vehicle that normally travels at speeds less than:',
    options: ['50 km/h', '40 km/h', '70 km/h', '60 km/h'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 33,
    text: 'A driver attempting to turn left at a steady green traffic light',
    options: ['Has the right-of-way over oncoming traffic', 'Must stop before the pedestrian crosswalk line, then proceed', 'Has the right-of-way over pedestrians', 'Must not cross the path of an approaching vehicle unless it is safe'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 34,
    text: 'A “Weave Zone” is best described as:',
    options: ['A place on a highway where the entrance and exit lanes are close together, and vehicles that are slowing to exit the highway must drive between vehicles that are accelerating to enter the highway', 'An area on the highway that is used for emergency parking', 'A place where the highway has a series of curves that require the driver to slow down', 'A limited access area on a highway where a driver can legally back their vehicle when they miss their exit'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 35,
    text: 'A road test will be refused by a driver examiner if:',
    options: ['The vehicle has a seating capacity greater than 7', 'The vehicle is registered as a farm vehicle', 'The vehicle is a rental vehicle', 'The vehicle is not mechanically safe'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 36,
    text: 'Is a driver allowed to exceed the speed limit when passing another vehicle?',
    options: ['Only when passing more than one vehicle at a time', 'Yes, but not more than 10 km/h over the speed limit', 'Yes, on primary highways only', 'No, it is never permitted'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 37,
    text: 'This sign indicates:',
    options: ['Children playing in residential area', 'Pedestrian crosswalk ahead', 'School area', 'Playground zone'],
    img: './assets/37.png', 
    answerIndex: 3
  },
  {
    id: 38,
    text: 'The speed limit in a construction zone must be obeyed:',
    options: ['Only during daylight hours', 'Only when a flag person is posted nearby', 'At all times', 'Only when construction workers are present'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 39,
    text: 'In a vehicle that does not have anti-lock brakes, threshold braking refers to:',
    options: ['Applying an even braking force to the point just before the wheels lock, and easing off slightly if the wheels lock', 'Pumping the brake pedal repeatedly until the vehicle comes to a stop', 'Releasing pressure on the brake pedal, then reapplying pressure until the wheels lock', 'Applying enough force to the brake pedal to lock the wheels and keep them locked'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 40,
    text: 'When a driver’s vehicle is approached by an emergency vehicle sounding a siren, from any direction on a two-way road, the driver must:',
    options: ['Continue at a reduced speed and allow sufficient space for the emergency vehicle to pass', 'Turn on their four-way hazard lights', 'Stop only when the emergency vehicle has difficulty in passing', 'Drive as close as possible to the right curb or edge of the road and stop'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 41,
    text: 'The recommended braking procedure to bring a vehicle to a normal, smooth stop is:',
    options: ['Continually increase brake pressure until the wheels lock', 'Shift the vehicle into neutral and apply steady pressure to the brake pedal until the vehicle comes to a stop', 'Pump the brake pedal smoothly until the vehicle stops', 'Apply brake pressure, ease off slightly just before the vehicle stops, then re-apply sufficient pressure to stop'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 42,
    text: 'In Alberta, the law requires that seat belts must be worn:',
    options: ['Only while carrying passengers', 'Properly at all times', 'Only when driving at highway speeds ', 'Only when driving in the city'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 43,
    text: 'This sign indicates:',
    options: ['Heavy trucks only', 'Do not pass', 'No vehicles allowed ', 'Do not enter'],
    img: './assets/43.png', 
    answerIndex: 1
  },
  {
    id: 44,
    text: 'A driver entering a traffic circle:',
    options: ['Must yield to traffic on the right', 'Must yield to vehicles in the traffic circle', 'Has the right-of-way ', 'Must use the courtesy rule and yield to the vehicle that arrived first'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 45,
    text: 'A deceleration lane is:',
    options: ['A lane where vehicles reduce speed when exiting a highway', 'The right lane of a four-lane highway for use by slower moving vehicles', 'The lane immediately to the right of a solid yellow line on a highway', 'A lane on hills for use by slower moving vehicles'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 46,
    text: 'When turning left from a two-way road onto a one-way road, what lane should the vehicle enter?',
    options: ['The lane immediately to the right of centre', 'The lane nearest the curb on the left side of the one-way road', 'The centre lane of the one-way road', 'The right lane of the one-way road'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 47,
    text: 'When two vehicles approach an uncontrolled “T” intersection at right angles to each other at approximately the same time:',
    options: ['The vehicle on the left must yield the right-of-way', 'The vehicle on the left has the right-of-way', 'The vehicle going straight through always has the right-of-way', 'Both vehicles must stop'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 48,
    text: 'Unless otherwise permitted, what lane must a driver be in to turn left from a one-way road?',
    options: ['The lane to the right of centre', 'The left lane nearest the left curb', 'The lane closes to the right curb', 'The centre lane'],
    img: null, 
    answerIndex: 1
  },
   {
    id: 49,
    text: 'Which statement describes the steps required to reverse in a straight line?',
    options: ['Keep both hands on the steering wheel and constantly use the inside rear-view mirror for guidance', 'Place the right hand at the top of the steering wheel, look over the left shoulder and use the outside left rear-view mirror', 'Place the left hand at the top of the steering wheel and look over the right shoulder through the rear window', 'Place the left hand at the bottom of the steering wheel and use both outside mirrors'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 50,
    text: 'An acceleration lane is:',
    options: ['A lane with broken line markings', 'A lane on hills, designated for faster moving vehicles', 'The left lane of a four-lane highway, for use when passing slow moving vehicles', 'A lane in a merge area for use by vehicles entering the highway'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 51,
    text: 'This sign indicates:',
    options: ['Lane ends, loss of right lane', 'Added lane (free flow)', 'Curve right', 'Dual turn lane ahead'],
    img: './assets/50.png', 
    answerIndex: 0
  },
   {
    id: 52,
    text: 'Under the Graduated Driver Licensing program, a learner with a Class 7 licence is not permitted to drive a vehicle between the hours of:',
    options: ['9:00 A.M. and 5:00 P.M.', 'Midnight and 5:00 A.M.', '10:00 P.M. and 6:00 A.M.', 'Midnight and 7:00 A.M.'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 53,
    text: 'What does a flashing yellow traffic light mean?',
    options: ['The traffic light will turn green', 'Stop until the light stops flashing', 'The traffic light will turn red', 'Proceed with caution'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 54,
    text: 'Unless otherwise posted, the school zone speed limits are in effect on school days during the following hours:',
    options: ['8:30 A.M. to 1 hour after sunset', '8:00 A.M. to 9:30 A.M., 11:30 A.M. to 1:30 P.M. and 3:00 P.M. to 4:30 P.M.', '8:30 A.M. to 9:30 A.M., 12:00 P.M. to 1:00 P.M. and 3:30 P.M. to 4:00 P.M.', '8:00 A.M. to 4:30 P.M.'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 55,
    text: 'This sign indicates:',
    options: ['Playground zone', 'Pedestrian crosswalk', 'Designated school bus stop', 'School zone'],
    img: './assets/55.png', 
    answerIndex: 1
  },
  {
    id: 56,
    text: 'When driving in the right lane on a highway, what should a driver do if another vehicle attempts to merge from an acceleration lane on the right?',
    options: ['Stop to allow the other vehicle in', 'Accelerate to close any gap in the traffic flow', 'Move into the left lane if it is safe to do so, or be prepared to adjust speed', 'Insist that the other vehicle yield the right-of-way'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 57,
    text: 'Which direction should a vehicle’s front wheels be turned when parked uphill on a two-way road with a curb?',
    options: ['Parallel to the curb', 'To the left', 'To the right', 'Straight ahead'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 58,
    text: 'When approaching the rear of a stopped school bus that has its alternately flashing red lights activated, a driver must:',
    options: ['Sound the horn to alert any pedestrians and pass at a slow rate of speed', 'Stop behind the bus and wait until the alternating red lights stop flashing', 'Move to the left lane, reduce speed to 30 km/h and pass cautiously', 'Stop behind the bus, look carefully for pedestrians and, if clear, drive past the bus slowly'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 59,
    text: 'When travelling in urban areas, drivers should scan the road approximately:',
    options: ['25 to 30 seconds ahead', '12 to 15 seconds ahead', '20 to 30 seconds ahead', '1 to 6 seconds ahead'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 60,
    text: 'This sign indicates:',
    options: ['T intersection', 'Traffic lane ends', 'Concealed intersection', 'Trucks entering highway ahead'],
    img: './assets/60.png', 
    answerIndex: 0
  },
   {
    id: 61,
    text: 'This sign indicates:',
    options: ['Winding road ahead', 'Weave zone ahead', 'Slippery when wet', 'Lane changing permitted'],
    img: './assets/61.png', 
    answerIndex: 0
  },
  {
    id: 62,
    text: 'Before leaving a parallel parking space on the right side of a road:',
    options: ['Turn on the right signal', 'Shoulder check to the left', 'Shoulder check to the right', 'Use an arm signal straight out and up'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 63,
    text: 'When approaching the top of a hill on a two-lane highway:',
    options: ['Exceeding the speed limit is permitted', 'Flash the headlights to warn oncoming vehicles', 'Stay to the right portion of the lane', 'Keep close to the centre line to prevent others from passing'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 64,
    text: 'Playground zones are in effect:',
    options: ['From 8:30 A.M. to 1 hour after sunset', 'From 8:00 A.M. to 8:00 P.M.', 'During daylight hours and until 11:00 P.M.', 'Only when school lets out at noon and after 4:30 P.M.'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 65,
    text: 'This sign indicates:',
    options: ['“T” intersection', 'Winding road ahead', 'Merging traffic', 'Road narrows to the right'],
    img: null, 
    answerIndex: 2
  },
   {
    id: 66,
    text: 'According to the Basic Licence Driver’s Handbook, most skids are the result of:',
    options: ['Bad brakes', 'Icy roads', 'Bad tires', 'Driver error'],
    img: null, 
    answerIndex: 3
  },
   {
    id: 67,
    text: 'How close can a driver legally park a vehicle to a Stop sign?',
    options: ['10 metres', '5 metres', '3 metres', '1.5 metres'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 68,
    text: 'Parking in a space reserved for persons with disabilities is permitted:',
    options: ['When all other parking spaces are filled', 'While the driver is waiting in the vehicle ', 'When a driver stops for less than 5 minutes', 'When the vehicle has a proper identifying placard or licence plate'],
    img: null, 
    answerIndex: 3
  },
  {
    id: 69,
    text: 'At what minimum distance must headlights be dimmed at night when approaching another vehicle from behind?',
    options: ['60 metres', '300 metres ', '150 metres', '100 metres'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 70,
    text: 'When are dual turns permitted at an intersection?',
    options: ['Only when turning from a one-way road onto a one-way road', 'Only when indicated by pavement markings or traffic control signs ', 'Any time there are at least 2 lanes on the road being entered', 'Only when the road has 6 or more lanes'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 71,
    text: 'This sign indicates:',
    options: ['Through lane only', 'Only when indicated by pavement markings or traffic control signsPassing permitted ', 'No turns permitted', 'Two-way traffic'],
    img: './assets/71.png', 
    answerIndex: 3
  },
  {
    id: 72,
    text: 'Under the Graduated Driver Licensing program, a learner with a Class 7 operator’s license is suspended when they accumulate',
    options: ['15 demerits', '12 demerits', '8 demerits', '7 demerits'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 73,
    text: 'Speed limit signs indicate:',
    options: ['The speed you should travel under all driving conditions', 'The minimum speed permitted on all highways', 'The maximum speed permitted when conditions are favourable', 'The maximum speed permitted except when passing'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 74,
    text: 'An “uncontrolled intersection”:',
    options: ['Has a flashing yellow light for one direction of traffic and a flashing red light for the other direction of traffic', 'Has traffic signs but no traffic control signal lights ', 'Has no traffic control signal lights or traffic signs', 'Has Yield signs but no Stop signs'],
    img: null, 
    answerIndex: 2
  },
  {
    id: 75,
    text: 'This sign indicates:',
    options: ['Sharp left curve in the road ahead', 'Left turns are prohibited ', 'Vehicles may turn left or go straight', 'Double lane left turn'],
    img: './assets/75.png', 
    answerIndex: 3
  },
  {
    id: 76,
    text: 'When facing a Merge sign a driver should:',
    options: ['Stop at the entrance to the other highway and wait until a gap in traffic occurs before proceeding', 'Watch for a gap in traffic on the highway they are entering, adjust to highway speed in the acceleration lane and blend in smoothly', 'Stop and yield the right-of-way to traffic already on the highway', 'Reduce speed and proceed with caution'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 77,
    text: 'In reduced visibility due to fog, smoke or snow, use the:',
    options: ['Park lights', 'Headlights on high beam', 'Headlights on low beam', 'Daytime running lights'],
    img: null, 
    answerIndex: 2
  },
   {
    id: 78,
    text: 'This sign indicates:',
    options: ['Emergency vehicles entering roadway', 'The designated lane is closed', 'Railway crossing', 'Daytime running lightsDo not enter'],
    img: './assets/78.png', 
    answerIndex: 2
  },
  {
    id: 79,
    text: 'Is a driver allowed to turn right at a red traffic control light?',
    options: ['Yes, but only when a sign permits such a turn', 'Yes, if the driver reduces speed and there is no traffic or pedestrian in the intersection, and no sign that prohibits such a turn', 'Yes, if the driver stops and there is no traffic, pedestrian or sign that prohibits such a turn', 'No, right turns on a red traffic light are never permitted'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 80,
    text: 'This sign indicates:',
    options: ['Lane is reserved for use by the specified vehicles', 'One-way traffic only', 'Car pool vehicles only', 'Lane can be used for dangerous goods vehicles'],
    img: '/assets/80.png', 
    answerIndex: 0
  },
  {
    id: 81,
    text: 'When a vehicle is stopped to allow a pedestrian to cross the road, other vehicles that are approaching from the rear:',
    options: ['May pass the stopped vehicle if there is no oncoming traffic that may impede passing safely', 'Must not pass the stopped vehicle. It is illegal', 'May pass the stopped vehicle when eye contact is made with the pedestrian and the pedestrian stops', 'May pass the stopped vehicle when the pedestrian crossing signal has changed to “Don’t Walk”'],
    img: null, 
    answerIndex: 1
  },
  {
    id: 82,
    text: 'When a head restraint is raised to the proper position, the centre of the head restraint is:',
    options: ['Level with the top of the ears', 'Level with the top of the shoulders', 'Level with the neck', 'Level with the top of the head'],
    img: null, 
    answerIndex: 0
  },
  {
    id: 83,
    text: 'This sign indicates:',
    options: ['School zone ahead', 'Caution log trucks turning', 'Traffic control signal advance warning', 'Pedestrian crosswalk ahead'],
    img: './assets/83.png', 
    answerIndex:2
  },
  {
    id: 84,
    text: 'When a “PASSING LANE AHEAD” sign is posted, the driver of a slower moving vehicle should:',
    options: ['Use the right lane and reduce their speed to half the posted speed limit', 'Use the left lane only', 'Increase speed', 'Move into the right lane when safe'],
    img: null, 
    answerIndex:3
  },
  {
    id: 85,
    text: 'When approaching the rear of a school bus that is displaying alternating flashing amber lights, a driver should:',
    options: ['Be prepared to stop as this is a warning that the alternating flashing red lights will come on once the bus has stopped', 'Be prepared to stop as this could be a warning that there is a collision ahead and the traffic flow has stopped', 'Stop immediately because the school bus is loading or unloading students', 'Use caution as the school bus is approaching a railroad crossing and is about to stop'],
    img: null, 
    answerIndex:0
  },
  {
    id: 86,
    text: 'In the Basic Licence Driver’s Handbook, “nighttime” is defined as the period of time:',
    options: ['Beginning 1 hour before sunset and ending 1 hour after the following sunrise', 'Beginning at sunset and ending at the following sunrise', 'Between 7:00 P.M. and 7:00 A.M.','Beginning 1 hour after sunset and ending 1 hour before the following sunrise'],
    img: null, 
    answerIndex:3
  },
  {
    id: 87,
    text: 'This sign indicates:',
    options: ['No through traffic or right turns', 'Turn left or go straight through', 'Turn from a one-way road onto a one-way road','Straight through or right turn'],
    img: '/assets/87.png', 
    answerIndex:3
  },
   {
    id: 88,
    text: 'A vehicle exiting a parking lot onto a roadway:',
    options: ['Must stop only if there is a Stop sign', 'Must come to a complete stop', 'Must stop before crossing the sidewalk only if there are predestrians present','Is not required to stop'],
    img: null, 
    answerIndex:1
  },
     {
    id: 89,
    text: 'When approaching a railway crossing that has the flashing red light and bell activated:',
    options: ['Cross the tracks if the train is a minimum of 250 metres away', 'Reduce speed, check for trains, and then proceed across the tracks', 'Do not stop and do not change gears while crossing the tracks','Stop at least 5 metres from the nearest rail until the train has passed'],
    img: null, 
    answerIndex:3
  },
     {
    id: 90,
    text: 'The recommended method for travelling through a curve on a highway is:',
    options: ['Reduce speed before the curve, stay in the lane and gently accelerate after entering the curve', 'Maintain speed before the curve, brake in the curve, look ahead and accelerate out of the curve', 'Cover the brake pedal before the curve, counter-steer toward the outside of the lane and accelerate when exiting','Accelerate before the curve, cover the brake pedal in the curve and accelerate after leaving the curve'],
    img: null, 
    answerIndex:0
  },
    {
    id: 91,
    text: 'This sign indicates:',
    options: ['Yield the right-of-way to pedestrians and traffic', 'Dangerous goods vehicle placard: flammable gas','Slow moving vehicle','Warning, Yield sign ahead'],
    img: './assets/91.png', 
    answerIndex:2
  },
   {
    id: 92,
    text: 'Why is it not safe to pass a loaded log-haul truck that is turning?',
    options: ['The driver of the truck needs to fully concentrate on the turn and does not need the added responsibility of a passing vehicle', 'Loaded log-haul trucks obey the 6-second rule for turns','A danger zone exists as the overhang of the logs from the tail end of the truck could block all the lanes on a highway during the turn','It is illegal to do so'],
    img: null, 
    answerIndex:2
  },
  {
    id: 93,
    text: 'This sign indicates:',
    options: ['Playground zone ahead', 'Pedestrian crossing ahead','School bus stop ahead','School zone ahead'],
    img: './assets/93.png', 
    answerIndex:2
  },
{
    id: 94,
    text: 'When encountering a funeral processions that is crossing an intersection:',
    options: ['Sound the horn before proceeding', 'Proceed through the funeral procession only when there is sufficient space between vehicles','Yield the right-of-way and do not break through the procession','Flash the high beam headlights before proceeding'],
    img: null, 
    answerIndex:2
  },
  {
    id: 95,
    text: 'If the vehicle’s right tires travel off the paved portion of the roadway:',
    options: ['Apply the brakes and counter-steer to return to the paved portion of the roadway', 'Keep a firm grip on the steering wheel, ease off the gas pedal, and then gradually return to the paved portion of the roadway','Accelerate slightly, then turn the steering wheel sharply to return to the paved portion of the roadway','Move the vehicle completely off the paved portion, then steer back onto the paved portion of the roadway'],
    img: null, 
    answerIndex:2
  },
  {
    id: 96,
    text: 'The sign indicates:',
    options: ['A school zone', 'School crosswalk ahead','Playground zone','Pedestrian activated lights ahead'],
    img: './assets/96.png', 
    answerIndex:1
  },
  {
    id: 97,
    text: 'When completing an angle park, the vehicle’s front wheel must be within what maximum distance from the nearest curb?',
    options: ['100 cm', '50 cm','25 cm','75 cm'],
    img: null, 
    answerIndex:1
  },
  {
    id: 98,
    text: 'In an urban area, when a pedestrian is standing at a crosswalk with their arm raised at approximately right angles to their body and pointing to the opposite curb, what does this mean?',
    options: ['Drivers must stop and yield to the pedestrian', 'The pedestrian is visually impaired and needs assistance to cross the road','Drivers do not have to yield to the pedestrian until they step off the curb','Drivers should reduce speed to 20 km/h and be cautious when passing the pedestrian'],
    img: null, 
    answerIndex:0
  },
  {
    id: 99,
    text: 'This sign indicates:',
    options: ['Pavement ends', 'Construction zone ahead','Dead end','Hazard marker'],
    img: './assets/99.png', 
    answerIndex:2
  },
    {
    id: 100,
    text: 'This sign indicates:',
    options: ['Divided highway ends', 'Divided highway begins','Obstruction – pass on the left','Two-way traffic'],
    img: './assets/100.png', 
    answerIndex:1
  },
   {
    id: 101,
    text: 'When approaching an intersection displaying a steady yellow traffic light:',
    options: ['Proceed since the light is about to turn green', 'Yield to vehicles on the right','Stop before the intersection unless the stop cannot be made in safety','Increase speed to clear the intersection'],
    img: null, 
    answerIndex:2
  },
   {
    id: 102,
    text: 'A solid yellow line together with a broken yellow line marked on the centre of a highway means:',
    options: ['The road narrows ahead', 'If the solid line is on your side, passing is permitted','If the broken line is on your side, passing is not permitted','If the broken line is on your side, passing is permitted'],
    img: null, 
    answerIndex:3
  },
   {
    id: 103,
    text: 'At 9:00 A.M. on a school day, what is the maximum speed permitted in a school zone in a rural area, unless otherwise posted?',
    options: ['50 km/h', '20 km/h','40 km/h','30 km/h'],
    img: null, 
    answerIndex:3
  },
  {
    id: 104,
    text: 'On a two-lane highway, when passing other vehicles travelling in the same direction, a driver:',
    options: ['Should pass on the left when safe', 'Should pass on the right when safe','Should always travel 30 km/h faster than the vehicle being passed','Should not pull out to pass until they are within 3 metres of the vehicle being passed'],
    img: null, 
    answerIndex:0
  },
  {
    id: 105,
    text: 'On a two-lane highway, when passing other vehicles travelling in the same direction, a driver:',
    options: ['Off-road facilities', 'Hospital services','Hotel services','Primary highway route marker'],
    img: './assets/105.png', 
    answerIndex:1
  },
  {
    id: 106,
    text: 'What minimum distance from a fire hydrant must a vehicle be parked?',
    options: ['3 metres', '4 metres','2 metres','5 metres'],
    img: null, 
    answerIndex:3
  },
  {
    id: 107,
    text: 'This sign indicates:',
    options: ['Construction sign, flag person ahead', 'Construction sign, detour ahead','Caution, falling rock ahead','Construction sign, people working ahead'],
    img: './assets/107.png', 
    answerIndex:3
  },
  {
    id: 108,
    text: 'If a driver is being followed by a vehicle that is attempting to pass on a two-way road, the driver should:',
    options: ['Stay in the lane and move to the left portion of the lane', 'Stay in the lane and move to the right portion of the lane','Turn on the left signal and stay in the lane','Turn on the right signal and drive in the parking lane'],
    img: null, 
    answerIndex:1
  },

  {
    id: 109,
    text: "A \"parking lane\" as defined in the Basic Licence Driver's Handbook is:",
    options: ["A lane closest to the curb, where parking is permitted but there are no meters", "All of these responses are correct", "That portion of a primary highway between the edge of the roadway to the right of the direction of traffic and the nearest solid white line, not being the centre line, marked on the roadway", "A lane immediately beside a row of parking meters in an urban area"],
    img: null,
    answerIndex: 2
  },
  {
    id: 110,
    text: "A traffic-lane control light with an illuminated red \"X\" means:",
    options: ["Prepare for an emergency vehicle travelling in that lane", "Reduce speed to below 30 km/h", "Use that lane only for the purpose of making left turns", "Travel is not permitted in that lane"],
    img: "/assets/12.png",
    answerIndex: 3
  },
  {
    id: 111,
    text: "Does a parent have the right to withdraw a previously given \"parental consent\" for someone under 18 years of age with respect to an operator's licence?",
    options: ["Yes, provided the person is still under the age of 18 and is not self-supporting or married", "Yes, provided that both parents verbally agree to withdraw the consent at a registry office", "No, once it is given it remains in effect until the person reaches 18 years of age", "No, but another consent form must be signed by both parents after a licence suspension"],
    img: null,
    answerIndex: 0
  },
  {
    id: 112,
    text: "The term \"over-driving\" your headlights at night means:",
    options: ["The driver cannot see clearly for 1000 metres", "The low beam headlights are not set properly", "The driver is travelling at a speed which would not permit them to see an object in time to stop", "The driver has had the headlights on for too long a time"],
    img: null,
    answerIndex: 2
  },
  {
    id: 113,
    text: "According to the law in Alberta, it is the driver's responsibility to ensure that all passengers under what age are properly restrained in the vehicle?:",
    options: ["18 years", "24 years", "21 years", "16 years"],
    img: null,
    answerIndex: 3
  },
  {
    id: 114,
    text: "When parallel parking, the vehicle's wheels closest to the curb must be within what maximum distance?",
    options: ["80 centimetres from the curb", "30 centimetres from the curb", "10 centimetres from the curb", "50 centimetres from the curb"],
    img: null,
    answerIndex: 3
  },
  {
    id: 115,
    text: "Who is responsible and legally obligated to report any medical condition, change in health, or physical disability that may affect a driver's ability to drive",
    options: ["The driver's employer", "The driver", "The driver's doctor", "The driver's insurance company"],
    img: null,
    answerIndex: 1
  },
  {
    id: 116,
    text: "A large \"X\" painted on the pavement indicates:",
    options: ["Construction ahead", "The traffic lane is ending", "Railway crossing ahead", "Wild animal crossing ahead"],
    img: null,
    answerIndex: 2
  },
  {
    id: 117,
    text: "A \"slow-moving vehicle\" sign must be displayed on the rear of any vehicle that normally travels at speeds less than:",
    options: ["50 km/h", "40 km/h", "70 km/h", "60 km/h"],
    img: null,
    answerIndex: 1
  },
  {
    id: 118,
    text: "A \"Weave Zone\" is best described as:",
    options: ["A place on a highway where the entrance and exit lanes are close together, and vehicles that are slowing to exit the highway must drive between vehicles that are accelerating to enter the highway", "An area on the highway that is used for emergency parking", "A place where the highway has a series of curves that require the driver to slow down", "A limited access area on a highway where a driver can legally back their vehicle when they miss their exit"],
    img: null,
    answerIndex: 0
  },
  {
    id: 119,
    text: "When a driver's vehicle is approached by an emergency vehicle sounding a siren, from any direction on a two-way road, the driver must:",
    options: ["Continue at a reduced speed and allow sufficient space for the emergency vehicle to pass", "Turn on their four-way hazard lights", "Stop only when the emergency vehicle has difficulty in passing", "Drive as close as possible to the right curb or edge of the road and stop"],
    img: null,
    answerIndex: 3
  },
  {
    id: 120,
    text: "When two vehicles approach an uncontrolled \"T\" intersection at right angles to each other at approximately the same time:",
    options: ["The vehicle on the left must yield the right-of-way", "The vehicle on the left has the right-of-way", "The vehicle going straight through always has the right-of-way", "Both vehicles must stop"],
    img: null,
    answerIndex: 0
  },
  {
    id: 121,
    text: "Which direction should a vehicle's front wheels be turned when parked uphill on a two-way road with a curb?",
    options: ["Parallel to the curb", "To the left", "To the right", "Straight ahead"],
    img: null,
    answerIndex: 1
  },
  {
    id: 122,
    text: "According to the Basic Licence Driver's Handbook, most skids are the result of:",
    options: ["Bad brakes", "Icy roads", "Bad tires", "Driver error"],
    img: null,
    answerIndex: 3
  },
  {
    id: 123,
    text: "Under the Graduated Driver Licensing program, a learner with a Class 7 operator's license is suspended when they accumulate",
    options: ["15 demerits", "12 demerits", "8 demerits", "7 demerits"],
    img: null,
    answerIndex: 0
  },
  {
    id: 124,
    text: "An \"uncontrolled intersection\":",
    options: ["Has a flashing yellow light for one direction of traffic and a flashing red light for the other direction of traffic", "Has traffic signs but no traffic control signal lights ", "Has no traffic control signal lights or traffic signs", "Has Yield signs but no Stop signs"],
    img: null,
    answerIndex: 2
  },
  {
    id: 125,
    text: "When a \"PASSING LANE AHEAD\" sign is posted, the driver of a slower moving vehicle should:",
    options: ["Use the right lane and reduce their speed to half the posted speed limit", "Use the left lane only", "Increase speed", "Move into the right lane when safe"],
    img: null,
    answerIndex: 3
  },
  {
    id: 126,
    text: "In the Basic Licence Driver's Handbook, \"nighttime\" is defined as the period of time:",
    options: ["Beginning 1 hour before sunset and ending 1 hour after the following sunrise", "Beginning at sunset and ending at the following sunrise", "Between 7:00 P.M. and 7:00 A.M.", "Beginning 1 hour after sunset and ending 1 hour before the following sunrise"],
    img: null,
    answerIndex: 3
  },
  {
    id: 127,
    text: "If the vehicle's right tires travel off the paved portion of the roadway:",
    options: ["Apply the brakes and counter-steer to return to the paved portion of the roadway", "Keep a firm grip on the steering wheel, ease off the gas pedal, and then gradually return to the paved portion of the roadway", "Accelerate slightly, then turn the steering wheel sharply to return to the paved portion of the roadway", "Move the vehicle completely off the paved portion, then steer back onto the paved portion of the roadway"],
    img: null,
    answerIndex: 1
  },
  {
    id: 128,
    text: "When completing an angle park, the vehicle's front wheel must be within what maximum distance from the nearest curb?",
    options: ["100 cm", "50 cm", "25 cm", "75 cm"],
    img: null,
    answerIndex: 1
  },
  {
    id: 129,
    text: "What is the maximum speed limit in a school zone when children are present?",
    options: ["30 km/h", "40 km/h", "50 km/h", "20 km/h"],
    img: null,
    answerIndex: 0
  },
  {
    id: 130,
    text: "Using a hand-held cell phone while driving in Alberta:",
    options: ["Is permitted if the call is brief", "Is only prohibited on highways", "Is prohibited and can result in a fine and demerit points", "Is permitted if using a hands-free device"],
    img: null,
    answerIndex: 2
  },
  {
    id: 131,
    text: "A Class 7 learner driver must have a blood alcohol content of:",
    options: ["0.05% or less", "0.08% or less", "0.00% (zero tolerance)", "0.02% or less"],
    img: null,
    answerIndex: 2
  },
  {
    id: 132,
    text: "When driving in a playground zone, the maximum speed limit is:",
    options: ["30 km/h during posted hours", "40 km/h at all times", "50 km/h", "30 km/h at all times"],
    img: null,
    answerIndex: 0
  },
  {
    id: 133,
    text: "A Class 7 learner must be accompanied by a supervising driver who:",
    options: ["Is at least 18 years old with a valid Class 5 licence for at least 2 years", "Is at least 21 years old with any valid licence", "Holds a Class 7 licence", "Is at least 25 years old"],
    img: null,
    answerIndex: 0
  },
  {
    id: 134,
    text: "When can a Class 7 learner driver transport passengers?",
    options: ["Never, passengers are not permitted", "Only family members", "Only when accompanied by a supervising driver", "Anytime with a maximum of 2 passengers"],
    img: null,
    answerIndex: 2
  },
  {
    id: 135,
    text: "The demerit point threshold for a Class 7 learner before suspension is:",
    options: ["8 points", "15 points", "12 points", "5 points"],
    img: null,
    answerIndex: 0
  },
  {
    id: 136,
    text: "When approaching a school bus with flashing red lights stopped on your side of the road, you must:",
    options: ["Slow down to 30 km/h and pass carefully", "Stop and remain stopped until the lights stop flashing", "Stop only if children are visible", "Proceed with caution"],
    img: null,
    answerIndex: 1
  },
  {
    id: 137,
    text: "What does this sign indicate?",
    options: ["Playground zone ahead", "School zone ahead", "Children crossing area", "Park entrance"],
    img: "/assets/school-zone.png",
    answerIndex: 1
  },
  {
    id: 138,
    text: "Texting while driving in Alberta can result in:",
    options: ["A warning for first-time offenders", "A fine of $300 and 3 demerit points", "Only a fine with no demerit points", "Vehicle impoundment"],
    img: null,
    answerIndex: 1
  },
  {
    id: 139,
    text: "When must headlights be turned on?",
    options: ["Only at night", "One hour before sunset to one hour after sunrise, and when visibility is less than 150 metres", "30 minutes before sunset to 30 minutes after sunrise", "Anytime it is raining or snowing"],
    img: null,
    answerIndex: 1
  },
  {
    id: 140,
    text: "The \"N\" sign for new drivers must be displayed:",
    options: ["On the rear window or bumper", "On the front windshield", "On both front and rear", "On the driver's side door"],
    img: null,
    answerIndex: 0
  },
  {
    id: 141,
    text: "When driving in fog, you should:",
    options: ["Use high beam headlights", "Use low beam headlights and reduce speed", "Use parking lights only", "Turn off all lights to reduce glare"],
    img: null,
    answerIndex: 1
  },
  {
    id: 142,
    text: "A solid yellow line on your side of the center line means:",
    options: ["Passing is permitted with caution", "Passing is not permitted", "Slow moving vehicle lane", "Construction zone ahead"],
    img: null,
    answerIndex: 1
  },
  {
    id: 143,
    text: "When parking uphill with a curb, you should:",
    options: ["Turn wheels away from the curb", "Turn wheels toward the curb", "Leave wheels straight", "Turn wheels in any direction"],
    img: null,
    answerIndex: 0
  },
  {
    id: 144,
    text: "A flashing green light at an intersection means:",
    options: ["Proceed with caution, yielding to pedestrians", "The light is about to turn yellow", "Advanced green for turning", "Pedestrian controlled crossing"],
    img: null,
    answerIndex: 3
  },
  {
    id: 145,
    text: "What is the minimum following distance recommended in ideal conditions?",
    options: ["1 second", "2 seconds", "3 seconds", "4 seconds"],
    img: null,
    answerIndex: 1
  },
  {
    id: 146,
    text: "When changing lanes, you must:",
    options: ["Signal, check mirrors, and change lanes", "Signal, check mirrors, shoulder check, then change lanes when safe", "Shoulder check and change lanes", "Signal and change lanes immediately"],
    img: null,
    answerIndex: 1
  },
  {
    id: 147,
    text: "The speed limit in an alley is:",
    options: ["30 km/h", "20 km/h", "40 km/h", "50 km/h"],
    img: null,
    answerIndex: 1
  },
  {
    id: 148,
    text: "If you are involved in a collision with no injuries but over $2,000 in damage, you must:",
    options: ["Report it to police immediately", "Report it to your insurance company only", "File a report within 24 hours", "No report is necessary"],
    img: null,
    answerIndex: 0
  },
  {
    id: 149,
    text: "When approaching a pedestrian crosswalk with a pedestrian waiting to cross:",
    options: ["Slow down and proceed if clear", "Stop and allow the pedestrian to cross", "Sound your horn to alert the pedestrian", "Speed up to clear the crosswalk"],
    img: null,
    answerIndex: 1
  },
  {
    id: 150,
    text: "Anti-lock braking systems (ABS) help prevent:",
    options: ["Brake wear", "Wheel lock-up during hard braking", "Engine stalling", "Transmission damage"],
    img: null,
    answerIndex: 1
  },
  {
    id: 151,
    text: "When driving at night, if an oncoming vehicle has bright lights, you should:",
    options: ["Flash your high beams repeatedly", "Look toward the right edge of the road", "Close one eye", "Stare at the headlights"],
    img: null,
    answerIndex: 1
  },
  {
    id: 152,
    text: "A yellow diamond-shaped sign indicates:",
    options: ["Regulatory information", "Warning or caution", "Construction zone", "Railroad crossing"],
    img: null,
    answerIndex: 1
  },
  {
    id: 153,
    text: "The primary purpose of traffic laws is to:",
    options: ["Generate revenue", "Ensure safe and orderly movement of traffic", "Employ police officers", "Reduce insurance costs"],
    img: null,
    answerIndex: 1
  },
  {
    id: 154,
    text: "When entering a freeway, you should:",
    options: ["Stop and wait for an opening", "Signal and merge at a slower speed", "Use the acceleration lane to match the speed of traffic, then merge", "Enter immediately without signaling"],
    img: null,
    answerIndex: 2
  },
  {
    id: 155,
    text: "During winter driving, the recommended following distance should be increased to:",
    options: ["3 seconds", "4 seconds or more", "2 seconds", "5 seconds minimum"],
    img: null,
    answerIndex: 1
  },
  {
    id: 156,
    text: "If your vehicle begins to skid, you should:",
    options: ["Brake hard", "Steer in the direction you want to go and ease off the accelerator", "Accelerate to regain control", "Turn the steering wheel opposite to the skid"],
    img: null,
    answerIndex: 1
  },
  {
    id: 157,
    text: "A red circle with a line through it on a sign means:",
    options: ["Warning", "Caution ahead", "Prohibition or something not permitted", "Construction zone"],
    img: null,
    answerIndex: 2
  },
  {
    id: 158,
    text: "When parking downhill with a curb, turn your front wheels:",
    options: ["Away from the curb", "Toward the curb", "Straight", "Either direction"],
    img: null,
    answerIndex: 1
  },
  {
    id: 159,
    text: "At a four-way stop, which vehicle has the right-of-way?",
    options: ["The largest vehicle", "The vehicle that arrived first", "The vehicle on the right", "The vehicle going straight"],
    img: null,
    answerIndex: 1
  },
  {
    id: 160,
    text: "When two vehicles arrive at an uncontrolled intersection at the same time, who has the right-of-way?",
    options: ["The vehicle on the left", "The vehicle traveling faster", "The vehicle on the right", "The larger vehicle"],
    img: null,
    answerIndex: 2
  },
  {
    id: 161,
    text: "A broken white line on the road means:",
    options: ["No passing allowed", "Lane changes are permitted when safe", "Pedestrian crossing ahead", "Construction zone"],
    img: null,
    answerIndex: 1
  },
  {
    id: 162,
    text: "When turning left at an intersection with a green light, you must:",
    options: ["Turn immediately without stopping", "Yield to oncoming traffic and pedestrians", "Have the right-of-way over all other vehicles", "Wait for the advanced green arrow"],
    img: null,
    answerIndex: 1
  },
  {
    id: 163,
    text: "The octagon shape is used exclusively for:",
    options: ["Yield signs", "Stop signs", "Speed limit signs", "Warning signs"],
    img: null,
    answerIndex: 1
  },
  {
    id: 164,
    text: "When must you yield to emergency vehicles with flashing lights and sirens?",
    options: ["Only on highways", "Only at intersections", "At all times, pulling over to the right when safe", "Only if they are directly behind you"],
    img: null,
    answerIndex: 2
  },
  {
    id: 165,
    text: "A traffic light is red, but a police officer signals you to proceed. You should:",
    options: ["Wait for the green light", "Follow the officer's directions", "Proceed with caution only", "Stop and ask the officer"],
    img: null,
    answerIndex: 1
  },
  {
    id: 166,
    text: "When approaching a railway crossing with flashing lights but no train is visible, you should:",
    options: ["Proceed quickly across", "Stop and wait until the lights stop flashing", "Slow down and look both ways", "Sound your horn and cross"],
    img: null,
    answerIndex: 1
  },
  {
    id: 167,
    text: "Unless otherwise posted, the speed limit in a residential area is:",
    options: ["30 km/h", "40 km/h", "50 km/h", "60 km/h"],
    img: null,
    answerIndex: 2
  },
  {
    id: 168,
    text: "A green arrow pointing left means:",
    options: ["Left turns only when safe", "You may proceed left without stopping", "Yield before turning left", "Stop before turning left"],
    img: null,
    answerIndex: 1
  },
  {
    id: 169,
    text: "When parallel parking, you must park within what distance from the curb?",
    options: ["30 cm", "50 cm", "60 cm", "1 metre"],
    img: null,
    answerIndex: 1
  },
  {
    id: 170,
    text: "You may not park within what distance of a fire hydrant?",
    options: ["3 metres", "5 metres", "10 metres", "15 metres"],
    img: null,
    answerIndex: 1
  },
  {
    id: 171,
    text: "When being passed by another vehicle, you should:",
    options: ["Speed up to prevent passing", "Maintain your speed and position", "Move to the right shoulder", "Slow down significantly"],
    img: null,
    answerIndex: 1
  },
  {
    id: 172,
    text: "A white rectangular sign with black letters indicates:",
    options: ["Warning", "Regulatory or law", "Guidance or information", "Construction"],
    img: null,
    answerIndex: 1
  },
  {
    id: 173,
    text: "The maximum speed limit on a secondary highway in Alberta, unless otherwise posted, is:",
    options: ["80 km/h", "100 km/h", "90 km/h", "110 km/h"],
    img: null,
    answerIndex: 0
  },
  {
    id: 174,
    text: "When making a right turn, you should:",
    options: ["Turn into the left lane", "Turn wide into the center lane", "Turn into the right lane", "Turn into any available lane"],
    img: null,
    answerIndex: 2
  },
  {
    id: 175,
    text: "A pedestrian with a white cane is:",
    options: ["Lost and needs directions", "Blind or visually impaired", "A crossing guard", "A surveyor"],
    img: null,
    answerIndex: 1
  },
  {
    id: 176,
    text: "When driving in heavy rain, you should:",
    options: ["Use high beam headlights", "Increase speed to get through quickly", "Reduce speed and increase following distance", "Turn on hazard lights while moving"],
    img: null,
    answerIndex: 2
  },
  {
    id: 177,
    text: "You must signal your intention to turn how far in advance?",
    options: ["10 metres", "30 metres", "At least 15 metres or 5 seconds", "50 metres"],
    img: null,
    answerIndex: 2
  },
  {
    id: 178,
    text: "What does a double solid yellow line indicate?",
    options: ["Passing is permitted", "No passing in either direction", "Passing permitted on the right only", "Lane change permitted"],
    img: null,
    answerIndex: 1
  },
  {
    id: 179,
    text: "When parking on a hill without a curb, you should:",
    options: ["Turn wheels to the right", "Leave wheels straight", "Turn wheels to the left", "Set parking brake only"],
    img: null,
    answerIndex: 0
  },
  {
    id: 180,
    text: "A Class 7 licence holder is suspended if they accumulate how many demerit points?",
    options: ["5 points", "8 points", "12 points", "15 points"],
    img: null,
    answerIndex: 1
  },
  {
    id: 181,
    text: "When can you pass on the right?",
    options: ["Never", "When the vehicle ahead is turning left and there is sufficient room", "Anytime on a multi-lane road", "Only on one-way streets"],
    img: null,
    answerIndex: 1
  },
  {
    id: 182,
    text: "If you miss your exit on a freeway, you should:",
    options: ["Back up to the exit", "Stop and make a U-turn", "Continue to the next exit", "Cut across lanes quickly"],
    img: null,
    answerIndex: 2
  },
  {
    id: 183,
    text: "When driving through a construction zone, you should:",
    options: ["Maintain highway speed", "Obey posted speed limits and watch for workers", "Speed up to clear the zone quickly", "Change lanes frequently"],
    img: null,
    answerIndex: 1
  },
  {
    id: 184,
    text: "A flashing yellow light means:",
    options: ["Stop", "Proceed with caution", "Speed up", "Yield right-of-way"],
    img: null,
    answerIndex: 1
  },
  {
    id: 185,
    text: "You may not park within what distance of a crosswalk?",
    options: ["3 metres", "5 metres", "10 metres", "15 metres"],
    img: null,
    answerIndex: 1
  },
  {
    id: 186,
    text: "When entering a roundabout, you must:",
    options: ["Speed up to merge", "Yield to traffic already in the roundabout", "Stop and wait for an opening", "Enter without yielding"],
    img: null,
    answerIndex: 1
  },
  {
    id: 187,
    text: "The purpose of a shoulder check is to:",
    options: ["Check tire pressure", "Look in the blind spot", "Adjust mirrors", "Check the speedometer"],
    img: null,
    answerIndex: 1
  },
  {
    id: 188,
    text: "Hydroplaning occurs when:",
    options: ["Tires lose contact with the road due to water buildup", "The engine overheats", "Brakes fail", "Tires are over-inflated"],
    img: null,
    answerIndex: 0
  },
  {
    id: 189,
    text: "Black ice is most likely to form:",
    options: ["On bridges and overpasses", "In parking lots", "On gravel roads", "In residential areas"],
    img: null,
    answerIndex: 0
  },
  {
    id: 190,
    text: "When driving in snow, you should:",
    options: ["Use cruise control", "Increase following distance to 8-10 seconds", "Use high beam headlights", "Drive in the left lane"],
    img: null,
    answerIndex: 1
  },
  {
    id: 191,
    text: "If your vehicle breaks down on a highway, you should:",
    options: ["Stay in the vehicle with doors locked", "Move the vehicle to the shoulder and activate hazard lights", "Stand behind the vehicle to warn others", "Leave the vehicle in the travel lane"],
    img: null,
    answerIndex: 1
  },
  {
    id: 192,
    text: "High beam headlights should be dimmed within what distance of an oncoming vehicle?",
    options: ["100 metres", "150 metres", "200 metres", "300 metres"],
    img: null,
    answerIndex: 1
  },
  {
    id: 193,
    text: "When driving at night, you should:",
    options: ["Over-drive your headlights to see farther", "Drive at a speed that allows you to stop within the distance lit by headlights", "Use high beams at all times", "Follow other vehicles closely to use their lights"],
    img: null,
    answerIndex: 1
  },
  {
    id: 194,
    text: "Defensive driving means:",
    options: ["Driving aggressively to protect your position", "Anticipating potential hazards and driving to avoid them", "Honking at other drivers frequently", "Maintaining high speeds"],
    img: null,
    answerIndex: 1
  },
  {
    id: 195,
    text: "If you encounter a vehicle with headlights off at night, you should:",
    options: ["Flash your headlights once to alert them", "Follow closely", "Pass immediately", "Sound your horn continuously"],
    img: null,
    answerIndex: 0
  },
  {
    id: 196,
    text: "When driving in fog, you should avoid using:",
    options: ["Low beam headlights", "Windshield wipers", "High beam headlights", "Turn signals"],
    img: null,
    answerIndex: 2
  },
  {
    id: 197,
    text: "The safest way to exit a freeway is to:",
    options: ["Brake hard before the exit", "Signal, move to exit lane early, then reduce speed on deceleration lane", "Slow down in the travel lane", "Exit without signaling"],
    img: null,
    answerIndex: 1
  },
  {
    id: 198,
    text: "When your vehicle starts to skid on ice, you should:",
    options: ["Brake hard immediately", "Steer in the direction you want to go", "Accelerate quickly", "Turn the wheel sharply"],
    img: null,
    answerIndex: 1
  },
  {
    id: 199,
    text: "What is the legal minimum tire tread depth in Alberta?",
    options: ["1.0 mm", "1.6 mm", "2.0 mm", "3.0 mm"],
    img: null,
    answerIndex: 1
  },
  {
    id: 200,
    text: "When driving behind a large truck, you should:",
    options: ["Follow closely to stay in their slipstream", "Increase following distance as you cannot see ahead", "Flash headlights to alert the driver", "Pass on the right side"],
    img: null,
    answerIndex: 1
  },
].map(createQuestionWithTips);

const sampleQuestions = baseSampleQuestions;

const TOTAL_QUESTIONS = 500;

function createQuestionWithTips(question) {
  const options = Array.isArray(question?.options) ? question.options : [];
  const answerIndex = question?.answerIndex;
  const safeAnswerIndex = typeof answerIndex === 'number' ? answerIndex : null;
  const hasCorrectOption = safeAnswerIndex !== null && options[safeAnswerIndex] !== undefined;
  const correctOption = hasCorrectOption ? options[safeAnswerIndex] : 'Not configured';
  const customTip = PRACTICE_QUESTION_TIPS[question?.id];
  const fallbackSummary = `Question ${question?.id || '-'}: review the exact legal condition in the prompt before choosing.`;
  const fallbackCorrect = hasCorrectOption
    ? `The correct answer is "${correctOption}" because it matches the tested rule for this item.`
    : 'A correct explanation is not available for this question yet.';
  const fallbackReview = hasCorrectOption
    ? 'Compare each option with the exact wording of the question and eliminate options that only partially match.'
    : 'Review is unavailable until a valid answer is configured.';

  const optionTips = options.map((optionText, optionIndex) => {
    if (Array.isArray(customTip?.optionTips) && customTip.optionTips[optionIndex]) {
      return customTip.optionTips[optionIndex];
    }
    const optionLabel = String.fromCharCode(65 + optionIndex);
    if (optionIndex === safeAnswerIndex) {
      return `Option ${optionLabel} is correct because it matches the legal rule in this question.`;
    }
    return `Option ${optionLabel} is not correct for this question's condition. The expected answer is "${correctOption}".`;
  });

  return {
    ...question,
    tip: {
      summary: customTip?.summary || fallbackSummary,
      correctExplanation: customTip?.correctExplanation || fallbackCorrect,
      reviewExplanation: customTip?.reviewExplanation || fallbackReview,
      optionTips,
    },
  };
}

function buildAnswerTip(question, selectedIndex) {
  if (!question || !Array.isArray(question.options) || question.answerIndex === null) {
    return {
      status: 'neutral',
      title: 'Tip unavailable',
      detail: 'This item has no configured answer guide yet.',
    };
  }

  const correctIndex = question.answerIndex;
  const correctOption = question.options[correctIndex];
  const ruleHint = question.tip?.summary || 'Review the exact legal condition in this question.';
  const correctExplanation = question.tip?.correctExplanation;
  const reviewExplanation = question.tip?.reviewExplanation;

  if (typeof selectedIndex !== 'number') {
    return {
      status: 'neutral',
      title: 'Need a submitted answer',
      detail: `Submit an option first, then review why "${correctOption}" is the best match. ${ruleHint}`,
    };
  }

  if (selectedIndex === correctIndex) {
    return {
      status: 'correct',
      title: 'Correct choice',
      detail: correctExplanation || `"${correctOption}" matches the tested rule. ${ruleHint}`,
    };
  }

  const selectedOption = question.options[selectedIndex];
  return {
    status: 'incorrect',
    title: 'Review this choice',
    detail: `You picked "${selectedOption}". The correct answer is "${correctOption}". ${reviewExplanation || ruleHint}`,
  };
}

function buildOptionTip(question, optionIndex, submittedIndex) {
  if (!question || !Array.isArray(question.options)) return '';

  const optionTips = question.tip?.optionTips;
  if (Array.isArray(optionTips) && optionTips[optionIndex]) {
    return optionTips[optionIndex];
  }

  const correctOption = question.options[question.answerIndex];
  const optionText = question.options[optionIndex];
  const ruleHint = question.tip?.summary || 'Review this question carefully.';
  const optionLabel = String.fromCharCode(65 + optionIndex);

  if (optionIndex === question.answerIndex) {
    return `Option ${optionLabel} is correct: "${optionText}". ${ruleHint}`;
  }

  if (optionIndex === submittedIndex) {
    return `Option ${optionLabel} is not correct: "${optionText}" does not satisfy the tested condition. The correct answer is "${correctOption}". ${ruleHint}`;
  }

  return `Option ${optionLabel} is not the best match for this rule question. The accepted answer is "${correctOption}". ${ruleHint}`;
}

const initialQuestions = sampleQuestions.slice(0, TOTAL_QUESTIONS);

// const convertToQuestionFormat = (data) => ({
//   id: data._id + 1,
//   text: data.Question,
//   options: data.Answer ? data.Answer.map((a) => a.name) : [],
//   answerIndex: data.isCorrect - 1,
//   tip: data.Tip,
// });

function HomePage() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isPremium = isPremiumUser(user);
  
  const [questions] = useState(initialQuestions);
  const [showNavigator, setShowNavigator] = useState(false);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [fontScale] = useState(1);
  const [showPopularity] = useState(false);
  const quizBodyRef = useRef(null);
  const attemptBufferRef = useRef(new Map());
  const [testHistoryId, setTestHistoryId] = useState(null);
  const [isTipOpen, setIsTipOpen] = useState(false);
  const [weakQuestions, setWeakQuestions] = useState([]);
  const [weakQuestionsLoading, setWeakQuestionsLoading] = useState(false);

  const weakQuestionIds = useMemo(
    () => new Set(weakQuestions.map((item) => String(item.question_id))),
    [weakQuestions]
  );

  const q = questions[current];
  const qId = q && q.id;
  const isCurrentSubmitted = Boolean(submitted[qId]);
  const submittedAnswer = answers[qId];
  const currentTip = buildAnswerTip(q, submittedAnswer);
  /*
  const getQuestions = useCallback((id) => {
    fetch('/api/driverassessment/' + id)
      .then((res) => res.json())
      .then((data) => {
        const formatted = convertToQuestionFormat(data[0]);
        setQuestions((prev) => [...prev, formatted]);
        console.log(data);
      })
      .catch((err) => console.error('Error fetching data:', err));
  }, []);
  */
  useEffect(() => {
    //getQuestions(current + 1);
  }, []); // IGNORE

  useEffect(() => {
    if (!token || testHistoryId) return;
    logTestAccess(token, 'practice', 'Practice Questions', 'General')
      .then((history) => setTestHistoryId(history?.id || null))
      .catch(() => {});
  }, [token, testHistoryId]);

  const refreshWeakQuestions = useCallback(async () => {
    if (!token) return;
    setWeakQuestionsLoading(true);
    try {
      const data = await fetchWeakQuestions(token, { limit: 12, minFailures: 1, testType: 'practice' });
      setWeakQuestions(data.weakQuestions || []);
    } catch (_error) {
      // Keep quiz flow uninterrupted if analytics endpoint fails.
    } finally {
      setWeakQuestionsLoading(false);
    }
  }, [token]);

  const flushAttemptBuffer = useCallback(async () => {
    if (!token) return;

    const bufferedAttempts = Array.from(attemptBufferRef.current.entries()).map(([questionId, stats]) => ({
      questionId,
      attemptCount: stats.attemptCount,
      failureCount: stats.failureCount,
      successCount: stats.successCount
    }));

    if (bufferedAttempts.length === 0) return;
    attemptBufferRef.current.clear();

    try {
      await trackQuestionAttempts(token, bufferedAttempts, 'practice', 'Practice Questions', testHistoryId);
    } catch (_error) {
      // If tracking fails, discard this batch to avoid retry storms on serverless.
    }
  }, [token, testHistoryId]);

  useEffect(() => {
    if (!token) return;
    refreshWeakQuestions();
  }, [token, refreshWeakQuestions]);

  useEffect(() => {
    const focusQuestionId = location?.state?.focusQuestionId;
    if (!focusQuestionId) return;

    const focusIndex = questions.findIndex((item) => String(item.id) === String(focusQuestionId));
    if (focusIndex >= 0) {
      setCurrent(focusIndex);
      setSelected(null);
      setIsTipOpen(false);
    }
  }, [location?.state, questions]);
  const goNext = useCallback(() => {
    const nextIndex = current + 1;
    // Check if next question is locked for free users
    if (!isPremium && isQuestionLocked(nextIndex, user)) {
      setShowUpgradeModal(true);
      return;
    }
    setSelected(null);
    setCurrent((c) => Math.min(c + 1, questions.length - 1));
  }, [questions.length, current, isPremium, user]);

  const goPrev = useCallback(() => {
    setSelected(null);
    setCurrent((c) => Math.max(c - 1, 0));
  }, []);
  
  // Handle navigation to specific question (inline handlers used in JSX)

  function handleSelect(idx) {
    setSelected(idx);
  }

  // keyboard support for Enter/Space on option items is added in the JSX onKeyDown handlers

  const handleSubmit = useCallback(() => {
    if (selected === null) return; // ignore empty submit
    setAnswers((prev) => ({ ...prev, [qId]: selected }));
    setSubmitted((prev) => ({ ...prev, [qId]: true }));
    setIsTipOpen(true);

    const isCorrect = selected === q?.answerIndex;
    const questionKey = String(qId);
    const previous = attemptBufferRef.current.get(questionKey) || {
      attemptCount: 0,
      failureCount: 0,
      successCount: 0
    };

    attemptBufferRef.current.set(questionKey, {
      attemptCount: previous.attemptCount + 1,
      failureCount: previous.failureCount + (isCorrect ? 0 : 1),
      successCount: previous.successCount + (isCorrect ? 1 : 0)
    });

    if (attemptBufferRef.current.size >= 5) {
      flushAttemptBuffer();
    }
  }, [selected, qId, q, flushAttemptBuffer]);

  function score() {
    let correct = 0;
    let total = questions.length;
    questions.forEach((qq) => {
      const a = answers[qq.id];
      if (a === qq.answerIndex) correct += 1;
    });
    return { correct, wrong: total - correct, total };
  }

  const { correct, wrong, total } = score();
  const answeredCount = Object.keys(answers).length;
  const percent = Math.round((answeredCount / total) * 100);

  useEffect(() => {
    if (!token || !testHistoryId) return;
    if (answeredCount === 0) return;

    const status = answeredCount === total ? 'completed' : 'in-progress';

    if (answeredCount % 5 !== 0 && status !== 'completed') {
      return;
    }

    updateTestProgress(
      token,
      testHistoryId,
      status === 'completed' ? correct : null,
      total,
      percent,
      status
    ).catch(() => {});
  }, [token, testHistoryId, answeredCount, total, percent, correct]);

  useEffect(() => {
    if (answeredCount !== total || total === 0) return;

    flushAttemptBuffer()
      .then(() => refreshWeakQuestions())
      .catch(() => {});
  }, [answeredCount, total, flushAttemptBuffer, refreshWeakQuestions]);

  useEffect(() => {
    if (!token) return;

    const interval = window.setInterval(() => {
      flushAttemptBuffer();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [token, flushAttemptBuffer]);

  useEffect(() => {
    if (!token) return;

    const onUnload = () => {
      flushAttemptBuffer();
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [token, flushAttemptBuffer]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      } else if (e.key === 'Enter') {
        handleSubmit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, handleSubmit]);

  useEffect(() => {
    setIsTipOpen(false);
  }, [current]);

  // (Removed) bottom controls auto-collapse logic — not used currently

  // Resolve banner from public folder for background image
  // In development, PUBLIC_URL is usually empty, so we use relative path
  const bannerUrl = `${process.env.PUBLIC_URL || ''}/assets/banner.png`;
  const appRootStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(14, 22, 33, 0.45), rgba(14, 22, 33, 0.35)), url('${bannerUrl}')`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  };

  return (
  <>
  <Header />
  <div className="app-root" style={appRootStyle}>
    {/* Subscription Banner for Free Users */}
   
    
    {/* Locked Question Overlay */}
    {!isPremium && isQuestionLocked(current, user) && (
      <div className="locked-overlay">
        <div className="locked-content">
          <div className="lock-icon">🔒</div>
          <h2>Question Locked</h2>
          <p>This question is only available to Premium members.</p>
          <p>Upgrade now to access all {questions.length}+ practice questions!</p>
          <button className="unlock-btn" onClick={() => setShowUpgradeModal(true)}>
            Unlock All Questions
          </button>
        </div>
      </div>
    )}

      <main className="quiz-card">
        <div className="quiz-card" style={{ fontSize: `${fontScale}rem` }}>
        <div className="quiz-top">
          <button className="nav-btn" onClick={goPrev} disabled={current === 0} aria-label="Previous question">◀ Prev</button>
          <div className="question-title">
            Question {current + 1} of {questions.length}
            <div className="progress-inline" aria-hidden>
              <div className="progress-bar"><div className="progress" style={{ width: `${percent}%` }} /></div>
              <small>{percent}% answered</small>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {/* <button className="report-btn" onClick={() => console.log('Report question', q.id)} aria-label="Report a mistake">Report</button> */}
            <button className="nav-btn" onClick={goNext} disabled={current === questions.length - 1} aria-label="Next question">Next ▶</button>
          </div>
        </div>

        {/* mobile-only status: show counts near top on narrow viewports so bottom summary isn't hidden */}
        <div className="mobile-status" aria-hidden>
          <div>Correct: {correct}</div>
          <div>Wrong: {wrong}</div>
          <div>Unanswered: {total - answeredCount}</div>
        </div>

        <div className="weak-questions-panel">
          <div className="weak-questions-header">
            <strong>Weak Questions Review</strong>
            {weakQuestionsLoading ? (
              <span className="weak-questions-meta">Updating...</span>
            ) : (
              <span className="weak-questions-meta">{weakQuestions.length} to recheck</span>
            )}
          </div>
          {weakQuestions.length > 0 ? (
            <div className="weak-questions-list">
              {weakQuestions.slice(0, 8).map((item) => {
                const targetIndex = questions.findIndex((questionItem) => String(questionItem.id) === String(item.question_id));
                if (targetIndex < 0) return null;

                return (
                  <button
                    key={item.question_id}
                    className={`weak-question-chip ${targetIndex === current ? 'active' : ''}`}
                    onClick={() => {
                      setCurrent(targetIndex);
                      setSelected(null);
                      setIsTipOpen(false);
                    }}
                    type="button"
                  >
                    Q{targetIndex + 1} · {item.failures} fails
                  </button>
                );
              })}
              <button className="weak-question-refresh" type="button" onClick={refreshWeakQuestions}>
                Refresh
              </button>
            </div>
          ) : (
            <p className="weak-questions-empty">
              Keep practicing. Incorrect answers will appear here for fast review before the real exam.
            </p>
          )}
          {weakQuestionIds.has(String(qId)) && (
            <div className="weak-question-badge">This is one of your frequently missed questions.</div>
          )}
        </div>

        {/* controls moved to bottom bar for better UX */}

        <div className="quiz-body" ref={quizBodyRef}>
          <div className="quiz-main">
            <div className="question-text">{q.text}</div>
            {q.img && (
              <div className="question-image">
                <center><img src={q.img} alt="Question related" /></center>
              </div>
            )}
            <ul className="options-list">
            {q.options.map((opt, idx) => {
              const isSubmitted = submitted[q.id];
              const activeAnswer = isSubmitted ? answers[q.id] : selected;
              const isSelected = activeAnswer === idx;
              const isCorrect = q.answerIndex === idx;
              const showResult = isSubmitted && isSelected;
              const showTipTrigger = isSubmitted;
              const optionTip = buildOptionTip(q, idx, answers[q.id]);
              // fake popularity distribution when toggled
              const popularity = showPopularity ? Math.max(0, 100 - idx * 15 - (Math.random() * 10)) : null;
              return (
                <li
                  key={idx}
                  role="button"
                  tabIndex={0}
                  aria-disabled={isSubmitted}
                  className={`option ${isSelected ? 'selected' : ''} ${showResult ? (isCorrect ? 'correct' : 'incorrect') : ''} ${isSubmitted ? 'locked' : ''}`}
                  onClick={() => { if (!isSubmitted) handleSelect(idx); }}
                  onKeyDown={(e) => {
                    if (isSubmitted) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(idx);
                    }
                  }}
                >
                  <span className="opt-letter">{String.fromCharCode(65 + idx)}</span>
                  <span className="opt-text">{opt}</span>
                  {isSubmitted && isCorrect && <span className="opt-badge">✔</span>}
                  {isSubmitted && isSelected && !isCorrect && <span className="opt-badge">✖</span>}
                  {showTipTrigger && optionTip && (
                    <span className="tip-wrap">
                      <button
                        type="button"
                        className="tip-trigger"
                        aria-label="Show answer tip"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setIsTipOpen((prev) => !prev);
                        }}
                      >
                        i
                      </button>
                      <span className="tip-tooltip" role="tooltip">{optionTip}</span>
                    </span>
                  )}
                  {showPopularity && popularity !== null && (
                    <span className="popularity">{Math.round(popularity)}%</span>
                  )}
                </li>
              );
            })}
              {showNavigator && (
                <div className="navigator-backdrop" onClick={() => setShowNavigator(false)}>
                  <div className="navigator" role="dialog" aria-modal={true} onClick={(e) => e.stopPropagation()}>
                    <div className="navigator-header">Jump to question</div>
                    <div className="navigator-grid">
                      {questions.map((qq, i) => (
                        <button key={qq.id} className={`nav-num ${i === current ? 'active' : ''}`} onClick={() => { setCurrent(i); setShowNavigator(false); }}>
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    <div style={{textAlign:'right', marginTop:8}}><button onClick={() => setShowNavigator(false)}>Close</button></div>
                  </div>
                </div>
              )}
          </ul>

            <div style={{height:8}} />

            {isCurrentSubmitted && (
              <div className="mobile-tip-panel">
                <button
                  className="mobile-tip-toggle"
                  type="button"
                  onClick={() => setIsTipOpen((prev) => !prev)}
                  aria-expanded={isTipOpen}
                >
                  {isTipOpen ? 'Hide tip' : 'Show tip'}
                </button>
                {isTipOpen && (
                  <div className={`answer-tip-card ${currentTip.status}`}>
                    <strong>{currentTip.title}</strong>
                    <p>{currentTip.detail}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="actions">
            <button className="submit-btn" onClick={handleSubmit} disabled={selected === null || submitted[q.id]}>Submit</button>
            <button className="clear-btn" onClick={() => { setSelected(null); }} disabled={selected === null}>Clear</button>
          </div>
        </div>

        <div className="quiz-bottom">
          <div className="summary">
            {/* <div>Answered: {Object.keys(answers).length} / {total}</div> */}
            {/* <div className="score">Correct: {correct} • Wrong: {wrong}</div> */}
          </div>
          <div className="jump-list">
            {/* show up to 9 unique nearby numbers centered on current when possible */}
            {(() => {
              const displayCount = Math.min(9, questions.length);
              // try to center current; but clamp to valid range so no duplicates
              let start = current - Math.floor(displayCount / 2);
              start = Math.max(0, Math.min(start, questions.length - displayCount));
              const items = [];
              for (let i = 0; i < displayCount; i++) {
                const idx = start + i;
                const qq = questions[idx];
                const ans = answers[qq.id];
                const answeredClass = ans === undefined ? 'unanswered' : (ans === qq.answerIndex ? 'correct' : 'incorrect');
                items.push(
                  <button key={qq.id} className={`jump-btn ${idx === current ? 'active' : ''} ${answeredClass}`} onClick={() => setCurrent(idx)} aria-label={`Jump to question ${idx+1}`}>
                    {idx + 1}
                  </button>
                );
              }
              return items;
            })()}
            <button className="jump-btn" onClick={() => setShowNavigator(true)}>All</button>
          </div>
        </div>
        </div>
      </main>
      {/* floating bottom controls moved outside quiz-card so they don't cover content */}
  
    
    </div>
    
    {/* Upgrade Modal */}
    <UpgradeModal 
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      feature="practice questions"
      currentCount={current + 1}
      limitCount={50}
    />
    
    </>
  );
  
}

export default HomePage;
